<template>
  <div class="page-container">
    <div class="main">
      <page
          :url="url"
          :form="form"
          :paginationSwitch="false"
          ref="pageRef"
          @reset="reset"
      >
        <template slot="tableSearch">
          <el-form-item :label="$t('text.csShiftType')">
            <el-select
                v-model="form.shiftType"
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 200px"
            >
              <el-option :label="$t('text.csShiftMorning')" value="MORNING"/>
              <el-option :label="$t('text.csShiftNight')" value="NIGHT"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftStaff')">
            <el-select
                v-model="form.messagingAppUserId"
                filterable
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 180px"
            >
              <el-option
                  v-for="user in appUserInfo"
                  :key="user.id"
                  :label="formatUserOption(user)"
                  :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enabled')">
            <el-select
                v-model="form.enabled"
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 120px"
            >
              <el-option :label="$t('creditFlag.01')" :value="true"/>
              <el-option :label="$t('creditFlag.00')" :value="false"/>
            </el-select>
          </el-form-item>
        </template>

        <template slot="buttonArea">
          <el-button type="success" size="small" icon="el-icon-sunrise" @click="openEditQuick('MORNING')">
            {{ $t('text.csShiftQuickAddMorning') }}
          </el-button>
          <el-button type="warning" size="small" icon="el-icon-moon" @click="openEditQuick('NIGHT')">
            {{ $t('text.csShiftQuickAddNight') }}
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openBatchAdd">
            {{ $t('text.csShiftBatchAdd') }}
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-user" plain @click="openUserManage">
            {{ $t('text.existingUsers') }}
          </el-button>
          <el-button type="info" size="small" :loading="onDutyLoading" @click="loadOnDuty">
            {{ $t('text.csShiftOnDutyPreview') }}
          </el-button>
        </template>

        <template slot="pageHeader">
          <el-alert
              :title="$t('text.csShiftFixedTimeHint')"
              type="info"
              :closable="false"
              show-icon
              style="margin: 8px 0 12px"
          />
          <el-alert
              v-if="onDutyText"
              :title="onDutyText"
              type="success"
              show-icon
              :closable="true"
              style="margin-bottom: 12px"
              @close="onDutyText = ''"
          />
        </template>

        <template slot="tableColumn">
          <el-table-column prop="shiftName" :label="$t('text.csShiftName')" width="120" align="center" header-align="center"/>
          <el-table-column :label="$t('text.csShiftTimeRange')" min-width="200" align="center" header-align="center">
            <template slot-scope="scope">
              {{ formatShiftRange(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column prop="daysOfWeek" :label="$t('text.csShiftDayOfWeek')" min-width="180" align="center" header-align="center">
            <template slot-scope="scope">
              {{ formatDaysOfWeek(scope.row.daysOfWeek) }}
            </template>
          </el-table-column>
          <el-table-column prop="userName" :label="$t('text.csShiftStaff')" width="160" align="center" header-align="center"/>
          <el-table-column prop="enabled" :label="$t('text.enabled')" width="100" align="center" header-align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.enabled ? 'success' : 'info'" size="small">
                {{ scope.row.enabled ? $t('creditFlag.01') : $t('creditFlag.00') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('text.actions')" width="180" align="center" header-align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="primary" size="mini" plain @click="openEdit(scope.row)">
                {{ $t('text.edit') }}
              </el-button>
              <el-button type="danger" size="mini" plain @click="handleDelete(scope.row)">
                {{ $t('text.delete') }}
              </el-button>
            </template>
          </el-table-column>
        </template>
      </page>

      <el-dialog
          :visible.sync="editVisible"
          :title="editForm.id ? $t('text.edit') : $t('text.add')"
          width="720px"
          @close="closeEdit"
      >
        <el-form ref="editFormRef" :model="editForm" :rules="rules" label-width="130px">
          <el-form-item :label="$t('text.csShiftType')" prop="shiftType">
            <el-radio-group v-model="editForm.shiftType" @change="applyShiftType">
              <el-radio-button label="MORNING">{{ $t('text.csShiftMorning') }}</el-radio-button>
              <el-radio-button label="NIGHT">{{ $t('text.csShiftNight') }}</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftTimeRange')">
            <el-input :value="formatShiftRange(editForm)" disabled/>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftDayOfWeek')">
            <div class="weekday-quick">
              <el-button size="mini" @click="applyWeekdayPreset('weekdays', 'editForm')">{{ $t('text.csShiftWeekdays') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('weekend', 'editForm')">{{ $t('text.csShiftWeekend') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('all', 'editForm')">{{ $t('text.csShiftEveryDay') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('clear', 'editForm')">{{ $t('text.clear') }}</el-button>
            </div>
            <el-select
                v-model="editForm.daysOfWeekList"
                multiple
                clearable
                collapse-tags
                :placeholder="$t('text.csShiftEveryDay')"
                style="width: 100%; margin-top: 8px"
            >
              <el-option v-for="item in dayOptions" :key="item.value" :label="item.label" :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftStaff')" prop="messagingAppUserId">
            <el-select
                v-model="editForm.messagingAppUserId"
                filterable
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 100%"
            >
              <el-option
                  v-for="user in appUserInfo"
                  :key="user.id"
                  :label="formatUserOption(user)"
                  :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enabled')">
            <el-switch v-model="editForm.enabled" :active-text="$t('creditFlag.01')" :inactive-text="$t('creditFlag.00')"/>
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="closeEdit">{{ $t('text.cancel') }}</el-button>
          <el-button type="primary" :loading="saving" @click="submitEdit">{{ $t('text.confirm') }}</el-button>
        </div>
      </el-dialog>

      <el-dialog
          :visible.sync="batchVisible"
          :title="$t('text.csShiftBatchAdd')"
          width="760px"
          @close="closeBatchAdd"
      >
        <el-form ref="batchFormRef" :model="batchForm" :rules="batchRules" label-width="130px">
          <el-form-item :label="$t('text.csShiftType')" prop="shiftType">
            <el-radio-group v-model="batchForm.shiftType" @change="applyBatchShiftType">
              <el-radio-button label="MORNING">{{ $t('text.csShiftMorning') }}</el-radio-button>
              <el-radio-button label="NIGHT">{{ $t('text.csShiftNight') }}</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftTimeRange')">
            <el-input :value="formatShiftRange(batchForm)" disabled/>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftDayOfWeek')">
            <div class="weekday-quick">
              <el-button size="mini" @click="applyWeekdayPreset('weekdays', 'batchForm')">{{ $t('text.csShiftWeekdays') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('weekend', 'batchForm')">{{ $t('text.csShiftWeekend') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('all', 'batchForm')">{{ $t('text.csShiftEveryDay') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('clear', 'batchForm')">{{ $t('text.clear') }}</el-button>
            </div>
            <el-select
                v-model="batchForm.daysOfWeekList"
                multiple
                clearable
                collapse-tags
                :placeholder="$t('text.csShiftEveryDay')"
                style="width: 100%; margin-top: 8px"
            >
              <el-option v-for="item in dayOptions" :key="item.value" :label="item.label" :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftStaff')" prop="messagingAppUserIds">
            <el-select
                v-model="batchForm.messagingAppUserIds"
                multiple
                filterable
                collapse-tags
                :placeholder="$t('text.csShiftBatchStaffPlaceholder')"
                style="width: 100%"
            >
              <el-option
                  v-for="user in appUserInfo"
                  :key="user.id"
                  :label="formatUserOption(user)"
                  :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enabled')">
            <el-switch v-model="batchForm.enabled" :active-text="$t('creditFlag.01')" :inactive-text="$t('creditFlag.00')"/>
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="closeBatchAdd">{{ $t('text.cancel') }}</el-button>
          <el-button type="primary" :loading="batchSaving" @click="submitBatchAdd">{{ $t('text.confirm') }}</el-button>
        </div>
      </el-dialog>

      <MessagingAppAtUserSelect
          v-if="showUserManage"
          :show.sync="showUserManage"
          :appUserInfo="appUserInfoFull"
          :roleList="roleList"
          @update-messaging-user-info="onMessagingUserUpdated"
      />
    </div>
  </div>
</template>

<script>
import Page from "@/components/Page.vue";
import MessagingAppAtUserSelect from "@/views/developer/components/messagingAppAt/MessagingAppAtUserSelect.vue";

const LAST_PRESET_KEY = 'csShiftScheduleLastPreset';

const SHIFT_PRESETS = {
  MORNING: {
    shiftType: 'MORNING',
    shiftName: '早班',
    startTime: '10:00:00',
    endTime: '22:00:00'
  },
  NIGHT: {
    shiftType: 'NIGHT',
    shiftName: '夜班',
    startTime: '22:00:00',
    endTime: '10:00:00'
  }
};

const WEEKDAY_PRESETS = {
  weekdays: [1, 2, 3, 4, 5],
  weekend: [6, 7],
  all: [],
  clear: []
};

const EMPTY_FORM = () => ({
  id: null,
  shiftType: 'MORNING',
  shiftName: SHIFT_PRESETS.MORNING.shiftName,
  startTime: SHIFT_PRESETS.MORNING.startTime,
  endTime: SHIFT_PRESETS.MORNING.endTime,
  daysOfWeek: '',
  daysOfWeekList: [],
  messagingAppUserId: null,
  enabled: true
});

const EMPTY_BATCH_FORM = () => ({
  shiftType: 'MORNING',
  shiftName: SHIFT_PRESETS.MORNING.shiftName,
  startTime: SHIFT_PRESETS.MORNING.startTime,
  endTime: SHIFT_PRESETS.MORNING.endTime,
  daysOfWeekList: [],
  messagingAppUserIds: [],
  enabled: true
});

const EMPTY_QUERY = () => ({
  shiftType: '',
  messagingAppUserId: '',
  enabled: ''
});

export default {
  name: 'CsShiftSchedule',
  components: {
    page: Page,
    MessagingAppAtUserSelect
  },
  data() {
    return {
      url: '/csShiftSchedule/selectList',
      form: EMPTY_QUERY(),
      appUserInfo: [],
      appUserInfoFull: [],
      roleList: [],
      showUserManage: false,
      editVisible: false,
      batchVisible: false,
      editForm: EMPTY_FORM(),
      batchForm: EMPTY_BATCH_FORM(),
      saving: false,
      batchSaving: false,
      onDutyLoading: false,
      onDutyText: '',
      dayOptions: [],
      rules: {
        shiftType: [{required: true, message: this.$t('message.chooseOne'), trigger: 'change'}],
        messagingAppUserId: [{required: true, message: this.$t('message.chooseOne'), trigger: 'change'}]
      },
      batchRules: {
        shiftType: [{required: true, message: this.$t('message.chooseOne'), trigger: 'change'}],
        messagingAppUserIds: [{type: 'array', required: true, min: 1, message: this.$t('text.csShiftBatchStaffPlaceholder'), trigger: 'change'}]
      }
    };
  },
  methods: {
    reset() {
      this.form.shiftType = '';
      this.form.messagingAppUserId = '';
      this.form.enabled = '';
    },
    loadLastPreset() {
      try {
        const raw = sessionStorage.getItem(LAST_PRESET_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    saveLastPreset(shiftType, daysOfWeekList) {
      sessionStorage.setItem(LAST_PRESET_KEY, JSON.stringify({shiftType, daysOfWeekList: daysOfWeekList || []}));
    },
    formatDaysOfWeek(daysOfWeek) {
      if (!daysOfWeek) {
        return this.$t('text.csShiftEveryDay');
      }
      return String(daysOfWeek).split(',')
          .map(day => this.formatDayOfWeek(Number(day.trim())))
          .join('、');
    },
    formatDayOfWeek(day) {
      if (!day) {
        return this.$t('text.csShiftEveryDay');
      }
      const found = this.dayOptions.find(item => item.value === day);
      return found ? found.label : day;
    },
    parseDaysOfWeekList(daysOfWeek) {
      if (!daysOfWeek) {
        return [];
      }
      return String(daysOfWeek).split(',')
          .map(day => Number(day.trim()))
          .filter(day => !Number.isNaN(day));
    },
    serializeDaysOfWeek(daysOfWeekList) {
      if (!daysOfWeekList || daysOfWeekList.length === 0) {
        return '';
      }
      return [...daysOfWeekList].sort((a, b) => a - b).join(',');
    },
    applyWeekdayPreset(presetKey, formName) {
      const target = this[formName];
      target.daysOfWeekList = [...(WEEKDAY_PRESETS[presetKey] || [])];
    },
    formatShiftRange(row) {
      if (!row || !row.startTime || !row.endTime) {
        return '-';
      }
      return `${row.startTime} - ${row.endTime} (${this.$t('text.csShiftBeijingTime')})`;
    },
    detectShiftType(row) {
      if (row.startTime === SHIFT_PRESETS.MORNING.startTime && row.endTime === SHIFT_PRESETS.MORNING.endTime) {
        return 'MORNING';
      }
      if (row.startTime === SHIFT_PRESETS.NIGHT.startTime && row.endTime === SHIFT_PRESETS.NIGHT.endTime) {
        return 'NIGHT';
      }
      return row.shiftName && row.shiftName.includes('夜') ? 'NIGHT' : 'MORNING';
    },
    applyShiftType(shiftType) {
      const preset = SHIFT_PRESETS[shiftType] || SHIFT_PRESETS.MORNING;
      this.editForm.shiftType = preset.shiftType;
      this.editForm.shiftName = preset.shiftName;
      this.editForm.startTime = preset.startTime;
      this.editForm.endTime = preset.endTime;
    },
    applyBatchShiftType(shiftType) {
      const preset = SHIFT_PRESETS[shiftType] || SHIFT_PRESETS.MORNING;
      this.batchForm.shiftType = preset.shiftType;
      this.batchForm.shiftName = preset.shiftName;
      this.batchForm.startTime = preset.startTime;
      this.batchForm.endTime = preset.endTime;
    },
    formatUserOption(user) {
      return user.name || '-';
    },
    loadUsers() {
      return this.$axios.get('/csShiftSchedule/staffOptions', {params: {}}).then(res => {
        this.appUserInfo = res.data || [];
      });
    },
    loadMessagingUsers() {
      return this.$axios.get('/messagingAppUserInfo/selectOption', {params: {}}).then(res => {
        const list = res.data || [];
        this.appUserInfoFull = list;
        this.roleList = [...new Set(list.map(item => item.role).filter(Boolean))];
      });
    },
    openUserManage() {
      this.loadMessagingUsers().finally(() => {
        this.showUserManage = true;
      });
    },
    onMessagingUserUpdated() {
      this.loadUsers();
      this.loadMessagingUsers();
    },
    loadOnDuty() {
      this.onDutyLoading = true;
      this.$axios.get('/csShiftSchedule/onDuty', {params: {}}).then(res => {
        const staffList = Array.isArray(res.data) ? res.data : [];
        const names = staffList.map(item => item.name).filter(Boolean);
        this.onDutyText = names.length > 0
            ? `${this.$t('text.csShiftOnDutyNow')} (${this.$t('text.csShiftBeijingTime')}): ${names.join(', ')}`
            : this.$t('text.csShiftOnDutyEmpty');
      }).finally(() => {
        this.onDutyLoading = false;
      });
    },
    openEditQuick(shiftType) {
      const lastPreset = this.loadLastPreset();
      this.editForm = EMPTY_FORM();
      this.applyShiftType(shiftType);
      if (lastPreset && Array.isArray(lastPreset.daysOfWeekList)) {
        this.editForm.daysOfWeekList = [...lastPreset.daysOfWeekList];
      }
      this.editVisible = true;
      this.$nextTick(() => {
        if (this.$refs.editFormRef) {
          this.$refs.editFormRef.clearValidate();
        }
      });
    },
    openEdit(row) {
      if (row) {
        this.editForm = {
          ...row,
          shiftType: this.detectShiftType(row),
          daysOfWeekList: this.parseDaysOfWeekList(row.daysOfWeek)
        };
      } else {
        this.openEditQuick('MORNING');
        return;
      }
      if (this.editForm.enabled === undefined || this.editForm.enabled === null) {
        this.editForm.enabled = true;
      }
      this.editVisible = true;
      this.$nextTick(() => {
        if (this.$refs.editFormRef) {
          this.$refs.editFormRef.clearValidate();
        }
      });
    },
    closeEdit() {
      this.editVisible = false;
      this.editForm = EMPTY_FORM();
    },
    openBatchAdd() {
      const lastPreset = this.loadLastPreset();
      this.batchForm = EMPTY_BATCH_FORM();
      if (lastPreset) {
        this.applyBatchShiftType(lastPreset.shiftType || 'MORNING');
        if (Array.isArray(lastPreset.daysOfWeekList)) {
          this.batchForm.daysOfWeekList = [...lastPreset.daysOfWeekList];
        }
      }
      this.batchVisible = true;
      this.$nextTick(() => {
        if (this.$refs.batchFormRef) {
          this.$refs.batchFormRef.clearValidate();
        }
      });
    },
    closeBatchAdd() {
      this.batchVisible = false;
      this.batchForm = EMPTY_BATCH_FORM();
    },
    submitEdit() {
      this.$refs.editFormRef.validate(valid => {
        if (!valid) {
          return;
        }
        this.applyShiftType(this.editForm.shiftType);
        this.saving = true;
        const payload = {
          id: this.editForm.id,
          shiftName: this.editForm.shiftName,
          startTime: this.editForm.startTime,
          endTime: this.editForm.endTime,
          daysOfWeek: this.serializeDaysOfWeek(this.editForm.daysOfWeekList),
          messagingAppUserId: this.editForm.messagingAppUserId,
          enabled: this.editForm.enabled,
          displayOrder: this.editForm.displayOrder || 0
        };
        const request = payload.id
            ? this.$axios.put('/csShiftSchedule/edit', payload)
            : this.$axios.post('/csShiftSchedule/add', payload);
        request.then(res => {
          if (res.code === 200) {
            this.saveLastPreset(this.editForm.shiftType, this.editForm.daysOfWeekList);
            this.$notify.success({title: this.$t('text.success'), message: res.msg});
            this.closeEdit();
            this.$refs.pageRef.onSubmit();
          } else {
            this.$notify.error({title: this.$t('text.error'), message: res.msg});
          }
        }).finally(() => {
          this.saving = false;
        });
      });
    },
    submitBatchAdd() {
      this.$refs.batchFormRef.validate(valid => {
        if (!valid) {
          return;
        }
        this.applyBatchShiftType(this.batchForm.shiftType);
        this.batchSaving = true;
        const payload = {
          shiftType: this.batchForm.shiftType,
          daysOfWeek: this.serializeDaysOfWeek(this.batchForm.daysOfWeekList),
          messagingAppUserIds: this.batchForm.messagingAppUserIds,
          enabled: this.batchForm.enabled
        };
        this.$axios.post('/csShiftSchedule/batchAdd', payload, {
          headers: {'Content-Type': 'application/json'},
          transformRequest: [(data) => JSON.stringify(data)]
        }).then(res => {
          if (res.code === 200) {
            this.saveLastPreset(this.batchForm.shiftType, this.batchForm.daysOfWeekList);
            this.$notify.success({title: this.$t('text.success'), message: res.msg});
            this.closeBatchAdd();
            this.$refs.pageRef.onSubmit();
          } else {
            this.$notify.error({title: this.$t('text.error'), message: res.msg});
          }
        }).finally(() => {
          this.batchSaving = false;
        });
      });
    },
    handleDelete(row) {
      this.$confirm(this.$t('text.confirmDeleteShift'), this.$t('text.delete'), {
        type: 'warning'
      }).then(() => {
        this.$axios.delete('/csShiftSchedule/delete', {params: {id: row.id}}).then(res => {
          if (res.code === 200) {
            this.$notify.success({title: this.$t('text.success'), message: res.msg});
            this.$refs.pageRef.onSubmit();
          } else {
            this.$notify.error({title: this.$t('text.error'), message: res.msg});
          }
        });
      }).catch(() => {});
    }
  },
  created() {
    this.dayOptions = [
      {value: 1, label: this.$t('text.weekday.mon')},
      {value: 2, label: this.$t('text.weekday.tue')},
      {value: 3, label: this.$t('text.weekday.wed')},
      {value: 4, label: this.$t('text.weekday.thu')},
      {value: 5, label: this.$t('text.weekday.fri')},
      {value: 6, label: this.$t('text.weekday.sat')},
      {value: 7, label: this.$t('text.weekday.sun')}
    ];
    this.loadUsers();
    this.$nextTick(() => {
      if (this.$refs.pageRef) {
        this.$refs.pageRef.onSubmit();
      }
    });
  }
};
</script>

<style lang="scss" scoped>
.weekday-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
